import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Plugs, Eye, EyeSlash, FloppyDisk, Lightning, CheckCircle, XCircle, CaretDown, ArrowsClockwise, Envelope, Plus, Trash } from '@phosphor-icons/react';
import { AcumbamailPanel } from './AcumbamailPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_ICONS = {
  validateid: { color: '#2563eb', icon: '🔏' },
  acumbamail: { color: '#059669', icon: '📧' },
  esendex: { color: '#d97706', icon: '📱' },
};

export function IntegrationsTab({ lang }) {
  const [services, setServices] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [forms, setForms] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [testing, setTesting] = useState({});
  const [saving, setSaving] = useState({});
  const [showAcumbaPanel, setShowAcumbaPanel] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/integrations/services`, { withCredentials: true })
      .then(r => {
        setServices(r.data || []);
        const f = {};
        for (const svc of r.data || []) {
          f[svc.service] = {};
          for (const field of svc.fields) {
            // Prefill non-secret fields with stored value so user can see/edit them
            const isSecret = ['api_key', 'api_secret', 'password', 'transactional_api_key', 'webhook_secret'].includes(field);
            f[svc.service][field] = isSecret ? '' : (svc.credentials_masked?.[field] || '');
          }
        }
        setForms(f);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (svcId) => {
    setSaving(p => ({ ...p, [svcId]: true }));
    try {
      const svc = services.find(s => s.service === svcId);
      const payload = { ...forms[svcId], enabled: svc?.enabled || false };
      await axios.put(`${API_URL}/api/integrations/${svcId}/credentials`, payload, { withCredentials: true });
      toast.success(lang === 'es' ? 'Credenciales guardadas' : 'Credentials saved');
      load();
    } catch (err) {
      toast.error(errText(err));
    }
    setSaving(p => ({ ...p, [svcId]: false }));
  };

  const handleToggle = async (svcId, enabled) => {
    try {
      await axios.put(`${API_URL}/api/integrations/${svcId}/toggle`, { enabled }, { withCredentials: true });
      toast.success(enabled
        ? (lang === 'es' ? 'Servicio activado' : 'Service enabled')
        : (lang === 'es' ? 'Servicio desactivado' : 'Service disabled'));
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const handleTest = async (svcId) => {
    setTesting(p => ({ ...p, [svcId]: true }));
    try {
      const r = await axios.post(`${API_URL}/api/integrations/${svcId}/test`, {}, { withCredentials: true });
      if (r.data.ok) {
        toast.success(r.data.message || 'OK');
      } else {
        toast.error(r.data.message || 'Error');
      }
      load();
    } catch (err) {
      toast.error(errText(err));
    }
    setTesting(p => ({ ...p, [svcId]: false }));
  };

  const isSecretField = (f) => ['api_key', 'api_secret', 'password', 'transactional_api_key', 'client_secret', 'subscription_password', 'webhook_secret'].includes(f);

  return (
    <div className="space-y-4" data-testid="integrations-tab">
      <div className="flex items-center gap-2 mb-2">
        <Plugs size={20} className="text-[#1f3e80]" />
        <span className="text-sm font-semibold text-stone-700 uppercase tracking-wider">
          {lang === 'es' ? 'Integraciones externas' : 'External integrations'}
        </span>
      </div>
      <p className="text-xs text-stone-500 -mt-2 mb-4">
        {lang === 'es'
          ? 'Configure las credenciales de los servicios externos. Los modulos se activaran automaticamente cuando las credenciales sean validas.'
          : 'Configure credentials for external services. Modules will be enabled automatically when credentials are valid.'}
      </p>
      {services.map(svc => {
        const si = SERVICE_ICONS[svc.service] || { color: '#6B7280', icon: '🔗' };
        const isOpen = expanded === svc.service;
        return (
          <Card key={svc.service} data-testid={`integration-${svc.service}`} className={`rounded-sm shadow-none border transition-colors ${svc.enabled ? 'border-emerald-200 bg-emerald-50/20' : 'border-stone-200'}`}>
            <CardContent className="p-0">
              {/* Header */}
              <div
                role="button"
                tabIndex={0}
                data-testid={`integration-toggle-${svc.service}`}
                onClick={() => setExpanded(isOpen ? null : svc.service)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(isOpen ? null : svc.service); }}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-stone-50/50 transition-colors text-left cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{si.icon}</span>
                  <div>
                    <span className="text-sm font-semibold text-stone-900">{svc.name}</span>
                    <p className="text-[11px] text-stone-500">{svc.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {svc.configured && (
                    <Badge variant="outline" className={`text-[10px] ${svc.last_test_ok ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : svc.last_test_ok === false ? 'bg-red-50 text-red-700 border-red-300' : 'bg-stone-50 text-stone-600'}`}>
                      {svc.last_test_ok ? (
                        <><CheckCircle size={10} className="mr-1" />{lang === 'es' ? 'Conectado' : 'Connected'}</>
                      ) : svc.last_test_ok === false ? (
                        <><XCircle size={10} className="mr-1" />{lang === 'es' ? 'Error' : 'Error'}</>
                      ) : (
                        lang === 'es' ? 'Configurado' : 'Configured'
                      )}
                    </Badge>
                  )}
                  {!svc.configured && (
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                      {lang === 'es' ? 'Sin configurar' : 'Not configured'}
                    </Badge>
                  )}
                  {svc.configured && (
                    <Switch
                      data-testid={`integration-switch-${svc.service}`}
                      checked={svc.enabled}
                      onCheckedChange={(v) => { handleToggle(svc.service, v); }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <CaretDown size={14} className={`text-stone-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Expanded config form */}
              {isOpen && (
                <div className="px-4 pb-4 border-t border-stone-200 space-y-4 pt-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {svc.fields.map(field => {
                      const isSecret = isSecretField(field);
                      const showVal = showSecrets[`${svc.service}-${field}`];
                      return (
                        <div key={field} className="space-y-1.5">
                          <Label className="text-xs">{svc.labels[field]}</Label>
                          <div className="relative">
                            <Input
                              data-testid={`integration-field-${svc.service}-${field}`}
                              type={isSecret && !showVal ? 'password' : 'text'}
                              placeholder={
                                svc.credentials_masked[field]
                                  || (field === 'api_url' && svc.service === 'validateid'
                                      ? 'https://vidsignercloud.validatedid.com/api/v2.2'
                                      : '')
                              }
                              value={forms[svc.service]?.[field] || ''}
                              onChange={e => setForms(p => ({ ...p, [svc.service]: { ...p[svc.service], [field]: e.target.value } }))}
                              className="pr-9 text-sm"
                            />
                            {isSecret && (
                              <button
                                type="button"
                                onClick={() => setShowSecrets(p => ({ ...p, [`${svc.service}-${field}`]: !showVal }))}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                              >
                                {showVal ? <EyeSlash size={14} /> : <Eye size={14} />}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      data-testid={`integration-save-${svc.service}`}
                      onClick={() => handleSave(svc.service)}
                      disabled={saving[svc.service]}
                      className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2 text-sm"
                    >
                      <FloppyDisk size={14} />
                      {saving[svc.service] ? (lang === 'es' ? 'Guardando...' : 'Saving...') : (lang === 'es' ? 'Guardar' : 'Save')}
                    </Button>
                    {svc.configured && (
                      <Button
                        variant="outline"
                        data-testid={`integration-test-${svc.service}`}
                        onClick={() => handleTest(svc.service)}
                        disabled={testing[svc.service]}
                        className="rounded-sm gap-2 text-sm border-[#1f3e80]/30 text-[#1f3e80]"
                      >
                        {testing[svc.service] ? <ArrowsClockwise size={14} className="animate-spin" /> : <Lightning size={14} />}
                        {testing[svc.service] ? (lang === 'es' ? 'Probando...' : 'Testing...') : (lang === 'es' ? 'Probar conexion' : 'Test connection')}
                      </Button>
                    )}
                    {svc.service === 'acumbamail' && svc.configured && svc.enabled && (
                      <Button
                        data-testid="acumbamail-manage-btn"
                        onClick={() => setShowAcumbaPanel(p => !p)}
                        className="rounded-sm gap-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <Envelope size={14} />
                        {lang === 'es' ? 'Gestionar Emails' : 'Manage Emails'}
                      </Button>
                    )}
                  </div>
                  {svc.last_test && (
                    <p className="text-[10px] text-stone-400">
                      {lang === 'es' ? 'Ultimo test' : 'Last test'}: {new Date(svc.last_test).toLocaleString()} — {svc.last_test_ok ? '✓ OK' : '✗ Error'}
                    </p>
                  )}
                  {svc.service === 'acumbamail' && (
                    <AlternativeSendersManager lang={lang} />
                  )}
                  {svc.service === 'validateid' && (
                    <>
                      <VidsignerDevicesManager lang={lang} />
                      <IPReminderConfigPanel lang={lang} />
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Acumbamail Management Panel */}
      {showAcumbaPanel && (
        <Card className="border-emerald-200 bg-white rounded-sm shadow-none mt-4">
          <CardContent className="p-4">
            <AcumbamailPanel lang={lang} onClose={() => setShowAcumbaPanel(false)} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AlternativeSendersManager({ lang }) {
  const [defaultSender, setDefaultSender] = useState(null);
  const [alts, setAlts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/integrations/acumbamail/senders`, { withCredentials: true });
      setDefaultSender(r.data?.default || null);
      setAlts(r.data?.alternatives || []);
    } catch (_e) { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const persist = async (list) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/integrations/acumbamail/senders`,
        { alternatives: list }, { withCredentials: true });
      setAlts(r.data?.alternatives || []);
      toast.success(lang === 'es' ? 'Remitentes actualizados' : 'Senders updated');
    } catch (e) {
      toast.error(errText(e, 'Error'));
    } finally { setSaving(false); }
  };

  const addSender = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email.includes('@')) return;
    if (alts.some(a => a.email === email)) {
      toast.error(lang === 'es' ? 'Ese email ya esta en la lista' : 'Email already on the list');
      return;
    }
    persist([...alts, { email, name: newName.trim() || email.split('@')[0] }]);
    setNewEmail(''); setNewName('');
  };

  const removeSender = (email) => {
    persist(alts.filter(a => a.email !== email));
  };

  return (
    <div className="border-t border-stone-200 pt-4 mt-2 space-y-3" data-testid="alt-senders-manager">
      <div>
        <p className="text-xs font-semibold text-stone-700 uppercase tracking-wider">
          {lang === 'es' ? 'Remitentes alternativos para emails transaccionales' : 'Alternative senders for transactional emails'}
        </p>
        <p className="text-[11px] text-stone-500 mt-0.5">
          {lang === 'es'
            ? 'Estos remitentes apareceran en el selector al notificar EAs o reenviar cuestionarios. El email tiene que estar autorizado/verificado en tu cuenta de Acumbamail; de lo contrario el envio sera rechazado.'
            : 'These senders will appear in the picker when notifying AEs or resending questionnaires. The email must be authorized/verified in your Acumbamail account or it will be rejected.'}
        </p>
      </div>

      {defaultSender?.email && (
        <div className="flex items-center justify-between text-xs bg-blue-50/50 border border-blue-200 rounded-sm px-3 py-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] bg-blue-100 text-blue-800 border-blue-300">
              {lang === 'es' ? 'Por defecto' : 'Default'}
            </Badge>
            <span className="font-mono text-stone-700">{defaultSender.email}</span>
            {defaultSender.name && <span className="text-stone-500">— {defaultSender.name}</span>}
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-stone-400">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p>
      ) : alts.length === 0 ? (
        <p className="text-xs text-stone-400 italic">{lang === 'es' ? 'No hay remitentes alternativos. El sistema usara el remitente por defecto.' : 'No alternatives configured. The default sender will be used.'}</p>
      ) : (
        <div className="space-y-1.5">
          {alts.map(a => (
            <div key={a.email} className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-sm px-3 py-1.5 text-xs" data-testid={`alt-sender-${a.email}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-stone-800">{a.email}</span>
                {a.name && <span className="text-stone-500">— {a.name}</span>}
              </div>
              <button type="button" onClick={() => removeSender(a.email)} className="text-stone-400 hover:text-red-600" disabled={saving} data-testid={`alt-sender-remove-${a.email}`}>
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 pt-1">
        <Input value={newEmail} onChange={e => setNewEmail(e.target.value)}
          placeholder={lang === 'es' ? 'Email autorizado en Acumbamail' : 'Email authorized in Acumbamail'}
          className="h-8 text-xs" type="email" data-testid="alt-sender-new-email" />
        <Input value={newName} onChange={e => setNewName(e.target.value)}
          placeholder={lang === 'es' ? 'Nombre visible (opcional)' : 'Display name (optional)'}
          className="h-8 text-xs" data-testid="alt-sender-new-name" />
        <Button size="sm" onClick={addSender} disabled={!newEmail.includes('@') || saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 gap-1" data-testid="alt-sender-add-btn">
          <Plus size={12} /> {lang === 'es' ? 'Anadir' : 'Add'}
        </Button>
      </div>
    </div>
  );
}


function VidsignerDevicesManager({ lang }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/vidsigner/devices`, { withCredentials: true });
      setDevices(r.data?.items || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/vidsigner/devices?sync=true`, { withCredentials: true });
      const items = r.data?.items || [];
      setDevices(items);
      toast.success(lang === 'es' ? `${items.length} tabletas sincronizadas` : `${items.length} tablets synced`);
    } catch (err) {
      toast.error(errText(err));
    }
    setLoading(false);
  };

  return (
    <div className="border-t border-stone-200 pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <Label className="text-xs font-semibold">{lang === 'es' ? 'Tabletas registradas' : 'Registered tablets'}</Label>
          <p className="text-[10px] text-stone-400">
            {lang === 'es'
              ? 'Tabletas dadas de alta en tu suscripcion de Validated ID. El equipo CRO eligira una al enviar a firmar.'
              : 'Tablets enrolled in your Validated ID subscription. The CRO team picks one when sending to sign.'}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={sync} disabled={loading}
          className="rounded-sm gap-1 h-8 text-xs" data-testid="vidsigner-devices-sync">
          <ArrowsClockwise size={12} className={loading ? 'animate-spin' : ''} />
          {loading ? (lang === 'es' ? 'Sincronizando...' : 'Syncing...') : (lang === 'es' ? 'Sincronizar' : 'Sync')}
        </Button>
      </div>
      {devices.length === 0 ? (
        <p className="text-xs text-stone-400 italic py-2">
          {lang === 'es' ? 'Sin tabletas sincronizadas. Pulsa "Sincronizar".' : 'No tablets synced. Press "Sync".'}
        </p>
      ) : (
        <div className="space-y-1" data-testid="vidsigner-devices-list">
          {devices.map(d => (
            <div key={d.device_name} className="flex items-center justify-between bg-violet-50/40 border border-violet-100 rounded-sm px-3 py-1.5">
              <div>
                <p className="text-xs font-medium text-stone-800">{d.device_description || d.device_name}</p>
                <p className="text-[10px] text-stone-400 font-mono">{d.device_name}</p>
              </div>
              {d.synced_at && (
                <span className="text-[10px] text-stone-400">
                  {lang === 'es' ? 'Sync' : 'Sync'}: {new Date(d.synced_at).toLocaleString()}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function IPReminderConfigPanel({ lang }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/ip-reminder/config`, { withCredentials: true });
      setCfg(r.data);
    } catch (_e) {
      toast.error(lang === 'es' ? 'Error cargando configuracion' : 'Error loading config');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/ip-reminder/config`, patch, { withCredentials: true });
      setCfg(r.data);
      toast.success(lang === 'es' ? 'Configuracion guardada' : 'Saved');
    } catch (e) {
      toast.error(errText(e, 'Error'));
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API_URL}/api/ip-reminder/run-now`, {}, { withCredentials: true });
      const n = r.data?.ips_notified ?? 0;
      const noPending = r.data?.no_pending;
      toast.success(
        noPending
          ? (lang === 'es' ? 'No hay firmas pendientes' : 'No pending signatures')
          : (lang === 'es' ? `Aviso enviado a ${n} IP(s)` : `Notified ${n} IP(s)`),
      );
    } catch (e) {
      toast.error(errText(e, 'Error'));
    } finally { setRunning(false); }
  };

  if (loading || !cfg) return null;

  return (
    <div className="mt-4 pt-4 border-t border-stone-200 space-y-3" data-testid="ip-reminder-config-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-xs font-semibold text-stone-700">{lang === 'es' ? 'Recordatorios al Investigador Principal' : 'Reminders to Principal Investigator'}</p>
          <p className="text-[11px] text-stone-500">
            {lang === 'es'
              ? 'Aviso diario por email y SMS al IP con sus consentimientos pendientes de firma. Hora local Europe/Madrid.'
              : 'Daily email/SMS to the PI listing pending consents. Time zone Europe/Madrid.'}
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            onChange={e => save({ enabled: e.target.checked })}
            disabled={saving}
            data-testid="ip-reminder-enabled"
          />
          {lang === 'es' ? 'Activo' : 'Active'}
        </label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <label className="flex flex-col gap-1">
          <span className="text-stone-600">{lang === 'es' ? 'Hora' : 'Hour'}</span>
          <input
            type="number" min={0} max={23}
            value={cfg.hour}
            onChange={e => setCfg(c => ({ ...c, hour: Number(e.target.value) || 0 }))}
            onBlur={e => save({ hour: Number(e.target.value) || 0 })}
            className="border border-stone-200 rounded px-2 py-1"
            data-testid="ip-reminder-hour"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-stone-600">{lang === 'es' ? 'Minuto' : 'Minute'}</span>
          <input
            type="number" min={0} max={59}
            value={cfg.minute}
            onChange={e => setCfg(c => ({ ...c, minute: Number(e.target.value) || 0 }))}
            onBlur={e => save({ minute: Number(e.target.value) || 0 })}
            className="border border-stone-200 rounded px-2 py-1"
            data-testid="ip-reminder-minute"
          />
        </label>
        <label className="flex items-center gap-2 mt-5">
          <input
            type="checkbox"
            checked={!!cfg.email_enabled}
            onChange={e => save({ email_enabled: e.target.checked })}
            disabled={saving}
            data-testid="ip-reminder-email-enabled"
          />
          Email
        </label>
        <label className="flex items-center gap-2 mt-5">
          <input
            type="checkbox"
            checked={!!cfg.sms_enabled}
            onChange={e => save({ sms_enabled: e.target.checked })}
            disabled={saving}
            data-testid="ip-reminder-sms-enabled"
          />
          SMS
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={runNow}
          disabled={running}
          className="text-xs bg-violet-700 text-white px-3 py-1.5 rounded hover:bg-violet-800 disabled:opacity-50"
          data-testid="ip-reminder-run-now"
        >
          {running ? (lang === 'es' ? 'Enviando...' : 'Sending...') : (lang === 'es' ? 'Probar ahora (forzar envio)' : 'Test now (force send)')}
        </button>
        <button
          type="button"
          onClick={async () => {
            setDiag(null);
            setDiagLoading(true);
            try {
              const r = await axios.get(`${API_URL}/api/ip-reminder/diagnose`, { withCredentials: true });
              setDiag(r.data);
            } catch (e) {
              toast.error(errText(e, 'Error'));
            } finally { setDiagLoading(false); }
          }}
          disabled={diagLoading}
          className="text-xs bg-stone-700 text-white px-3 py-1.5 rounded hover:bg-stone-800 disabled:opacity-50"
          data-testid="ip-reminder-diagnose"
        >
          {diagLoading ? (lang === 'es' ? 'Cargando...' : 'Loading...') : (lang === 'es' ? 'Diagnostico envios de hoy' : 'Diagnose today\'s sends')}
        </button>
      </div>
      {diag && (
        <div className="mt-2 space-y-2 text-[11px] bg-stone-50 border border-stone-200 rounded p-2" data-testid="ip-reminder-diag-result">
          <div><b>{lang === 'es' ? 'Fecha:' : 'Date:'}</b> {diag.today}</div>
          <div>
            <b>{lang === 'es' ? 'Indice unico activo:' : 'Unique index active:'}</b>{' '}
            {diag?.indexes?.uniq_date_ip?.unique ? '✅ Si' : '❌ NO — el lock atomico no funciona'}
          </div>
          <div>
            <b>{lang === 'es' ? 'Docs de hoy en ip_reminders_sent:' : 'Today docs:'}</b>{' '}
            {(diag.today_docs || []).length}
            {(diag.today_docs || []).length > 0 && (
              <ul className="ml-4 list-disc">
                {diag.today_docs.map((d, i) => (
                  <li key={i} className={d.send_calls > 1 ? 'text-rose-700 font-semibold' : ''}>
                    <code>{d.ip_email}</code> · {d.status} · sent_at={d.sent_at || '—'} · canales={JSON.stringify(d.channels || [])}
                    {d.send_calls != null && <> · <b>send_calls={d.send_calls}</b>{d.send_calls > 1 ? ' ⚠ DUPLICADO' : ''}</>}
                    {d.all_response_ids && d.all_response_ids.length > 0 && (
                      <ul className="ml-4 list-decimal">
                        {d.all_response_ids.map((e, j) => (
                          <li key={j}>at={e.at} · rids={JSON.stringify(e.response_ids || {})}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <b>{lang === 'es' ? 'Envios registrados en audit (hoy):' : 'Audit entries today:'}</b>{' '}
            {diag.audit_today_count}
            {diag.audit_today_count > 1 && (
              <span className="text-rose-700 font-semibold">
                {' '}— {lang === 'es' ? 'HAY DUPLICADO' : 'DUPLICATE DETECTED'}
              </span>
            )}
          </div>
          <div>
            <b>{lang === 'es' ? 'Agrupacion actual (IPs con pendientes ahora):' : 'Current groups:'}</b>
            <pre className="bg-white p-2 rounded border border-stone-200 overflow-x-auto text-[10px]">{JSON.stringify(diag.groups_now, null, 2)}</pre>
          </div>
          <details>
            <summary className="cursor-pointer text-stone-600">{lang === 'es' ? 'JSON completo' : 'Full JSON'}</summary>
            <pre className="bg-white p-2 rounded border border-stone-200 overflow-x-auto text-[10px]">{JSON.stringify(diag, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}
